import React, { useState, useEffect } from 'react';
import { trainingProgramService } from '../services/trainingProgram';
import { ProgramCreator } from './ProgramCreator';
import { ProgramList } from './ProgramList';
import { useAuth } from '../contexts/AuthContext';
import type { TrainingProgram } from '../types';

interface ProgramManagerProps {
  className?: string;
}

/**
 * ProgramManager - Main component for managing training programs
 * Handles program CRUD operations, active program management, and program-workout relationships
 * Requirements: 3.1, 3.2, 4.1, 4.2
 */
export const ProgramManager: React.FC<ProgramManagerProps> = ({ className = '' }) => {
  const { user, isAuthenticated } = useAuth();
  const [programs, setPrograms] = useState<TrainingProgram[]>([]);
  const [activeProgram, setActiveProgram] = useState<TrainingProgram | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showCreator, setShowCreator] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  // Load programs on component mount
  useEffect(() => {
    loadPrograms();
  }, []);

  const loadPrograms = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      console.log('Loading programs for user:', user?.id);
      
      if (!user?.id) {
        const errorMsg = 'Пользователь не авторизован';
        console.error(errorMsg);
        alert(errorMsg);
        setError(errorMsg);
        setIsLoading(false);
        return;
      }
      
      const [allPrograms, currentActiveProgram] = await Promise.all([
        trainingProgramService.getAllPrograms(user.id),
        trainingProgramService.getActiveProgram()
      ]);
      
      console.log('Programs loaded:', allPrograms.length);
      
      setPrograms(allPrograms);
      setActiveProgram(currentActiveProgram);
    } catch (err) {
      console.error('Failed to load programs:', err);
      const errorMsg = err instanceof Error ? err.message : 'Не удалось загрузить программы тренировок';
      setError(errorMsg);
      
      // Show alert for debugging
      alert(`Ошибка загрузки программ:\n${errorMsg}\n\nUser ID: ${user?.id || 'не определен'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateProgram = async (programData: {
    name: string;
    workouts: Array<{
      name: string;
      exercises: Array<{
        exerciseId: string;
        targetSets?: number;
        targetRepsMin?: number;
        targetRepsMax?: number;
        restSeconds?: number;
      }>;
    }>;
    setAsActive: boolean;
  }) => {
    try {
      setIsCreating(true);
      setError(null);

      // Check if in development mode
      if (import.meta.env.VITE_DEV_MODE === 'true') {
        console.log('Development mode: simulating program creation');
        
        // Simulate async operation
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Show success message and close creator
        setShowCreator(false);
        setError(null);
        setSuccessMessage(`Программа "${programData.name}" успешно создана в режиме разработки! 🎉`);
        
        // Clear success message after 5 seconds
        setTimeout(() => setSuccessMessage(null), 5000);
        
        console.log('Program created successfully in development mode:', programData.name);
        return;
      }

      console.log('Creating program:', programData.name);
      console.log('User context:', { userId: user?.id, isAuthenticated });

      // Create the program with user ID from auth context
      const newProgram = await trainingProgramService.createProgram(
        programData.name,
        programData.setAsActive,
        user?.id // Pass user ID from auth context
      );

      console.log('Program created, adding workouts...');

      // Add workouts and exercises
      for (const workoutData of programData.workouts) {
        const workout = await trainingProgramService.addWorkoutToProgram(
          newProgram.id,
          workoutData.name
        );

        console.log(`Workout "${workoutData.name}" added, adding exercises...`);

        // Add exercises to the workout
        for (const exerciseData of workoutData.exercises) {
          await trainingProgramService.addExerciseToWorkout(
            workout.id,
            exerciseData.exerciseId,
            exerciseData.targetSets,
            exerciseData.targetRepsMin,
            exerciseData.targetRepsMax,
            exerciseData.restSeconds
          );
        }
      }

      console.log('All workouts and exercises added successfully');

      // Reload programs to get the complete data
      await loadPrograms();
      setShowCreator(false);
      setSuccessMessage(`Программа "${programData.name}" успешно создана! 🎉`);
      
      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      console.error('Failed to create program:', err);
      
      // Get detailed error message
      const errorMessage = err instanceof Error ? err.message : 'Неизвестная ошибка';
      const detailedError = `Не удалось создать программу: ${errorMessage}`;
      
      setError(detailedError);
      
      // Show alert with error details for debugging
      alert(`❌ Ошибка создания программы\n\n${errorMessage}\n\nПроверьте консоль браузера для подробностей.`);
    } finally {
      setIsCreating(false);
    }
  };

  const handleRenameProgram = async (programId: string, newName: string) => {
    try {
      setError(null);
      await trainingProgramService.updateProgramName(programId, newName);
      await loadPrograms();
    } catch (err) {
      console.error('Failed to rename program:', err);
      setError('Не удалось переименовать программу');
    }
  };

  const handleDeleteProgram = async (programId: string) => {
    try {
      setError(null);
      
      // Check if program has workout history
      const historyCount = await trainingProgramService.getProgramWorkoutHistoryCount(programId);
      
      if (historyCount > 0) {
        // Show confirmation dialog for programs with history
        const confirmed = await new Promise<boolean>((resolve) => {
          const result = confirm(
            `Эта программа содержит ${historyCount} завершенных тренировок.\n\n` +
            `Выберите действие:\n` +
            `• OK - Удалить программу, но сохранить историю тренировок\n` +
            `• Отмена - Не удалять программу`
          );
          resolve(result);
        });
        
        if (!confirmed) {
          return; // User cancelled
        }
        
        // Delete with history preservation
        await trainingProgramService.deleteProgram(programId, {
          preserveHistory: true,
          confirmationCallback: async () => true // Already confirmed above
        });
        
        setSuccessMessage(`Программа удалена. История тренировок (${historyCount} сессий) сохранена.`);
      } else {
        // No history, simple confirmation
        const confirmed = confirm('Вы уверены, что хотите удалить эту программу?');
        if (!confirmed) {
          return;
        }
        
        await trainingProgramService.deleteProgram(programId, {
          preserveHistory: false
        });
        
        setSuccessMessage('Программа успешно удалена.');
      }
      
      await loadPrograms();
    } catch (err) {
      console.error('Failed to delete program:', err);
      setError(err instanceof Error ? err.message : 'Не удалось удалить программу');
    }
  };

  const handleSetActiveProgram = async (programId: string) => {
    try {
      setError(null);
      const updatedProgram = await trainingProgramService.setActiveProgram(programId);
      setActiveProgram(updatedProgram);
      await loadPrograms();
    } catch (err) {
      console.error('Failed to set active program:', err);
      setError('Не удалось активировать программу');
    }
  };

  const handleCancelCreation = () => {
    setShowCreator(false);
    setError(null);
    setSuccessMessage(null);
  };

  if (isLoading) {
    return (
      <div className={`program-manager ${className}`}>
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className={`program-manager ${className}`}>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-bold">📋 Мои программы</h1>
          {!showCreator && (
            <button
              onClick={() => setShowCreator(true)}
              className="btn-primary"
              disabled={isCreating}
            >
              + Создать программу
            </button>
          )}
        </div>

        {/* Active Program Indicator */}
        {activeProgram && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
            <div className="flex items-center">
              <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
              <span className="text-sm font-medium text-green-800">
                Активная программа: {activeProgram.name}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Success Message */}
      {successMessage && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
          <p className="text-sm text-green-700">{successMessage}</p>
        </div>
      )}

      {/* Program Creator */}
      {showCreator && (
        <div className="mb-6">
          <ProgramCreator
            onCreateProgram={handleCreateProgram}
            onCancel={handleCancelCreation}
            isCreating={isCreating}
          />
        </div>
      )}

      {/* Programs List */}
      {!showCreator && (
        <ProgramList
          programs={programs}
          activeProgram={activeProgram}
          onRenameProgram={handleRenameProgram}
          onDeleteProgram={handleDeleteProgram}
          onSetActiveProgram={handleSetActiveProgram}
        />
      )}

      {/* Empty State */}
      {!showCreator && programs.length === 0 && (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">📋</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Нет программ тренировок
          </h3>
          <p className="text-gray-500 mb-6">
            Создайте свою первую программу тренировок, чтобы начать отслеживать прогресс
          </p>
          <button
            onClick={() => setShowCreator(true)}
            className="btn-primary"
          >
            Создать первую программу
          </button>
        </div>
      )}
    </div>
  );
};

export default ProgramManager;